import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { TErrors, TResponseQueryError, useCheckVerificationCodeMutation } from '@alycecom/services';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { getIsCodeModalOpen } from '../../../store/oneTimeCodeVerification/oneTimeCodeVerification.selectors';
import {
  OneTimeCodeFieldNames,
  oneTimeCodeVerificationValidationSchema,
  TOneTimeCodeVerificationFields,
} from '../../../store/oneTimeCodeVerification/oneTimeCodeVerification.schemas';
import { getGiftHashId } from '../../../store/data/data.selectors';
import { claimRequest } from '../../../store/claim/claim.actions';
import { getRecipientState } from '../../../../../store/users/users.selectors';
import { getMaxVerificationAttemptReached } from '../../../store/statuses/statuses.selectors';
import SupportLink from '../../../../../components/Shared/SupportLink';
import { toggleModal } from '../../../store/oneTimeCodeVerification/oneTimeCodeVerification.reducer';
import { updateStatuses } from '../../../store/statuses/statuses.actions';
import useResponsive from '../../../../../hooks/useResponsive';
import VerificationCodeField from '../VerificationCodeField';
import useTrackRecipient from '../../../../../hooks/useTrackRecipient';
import { getBranding } from '../../../../../store/branding/branding.selectors';
import { BrandingFields } from '../../../../../types/Branding.types';

import ResendLink from './ResendLink';
import { styles } from './oneTimeCodeVerificationModal.styles';

const backdropStyles = {
  top: 0,
  left: 'initial',
  background: 'black',
  opacity: 0.8,
  width: '100%',
};

const OneTimeCodeVerificationModal = (): JSX.Element => {
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();

  const { isMobile } = useResponsive();
  const [hasVerificationError, setHasVerificationError] = useState(false);

  const hashId = useSelector(getGiftHashId);
  const isOpen = useSelector(getIsCodeModalOpen);
  const customBranding = useSelector(getBranding);
  const recipient = useSelector(getRecipientState);
  const isAttemptReached = useSelector(getMaxVerificationAttemptReached);

  const [checkVerificationCode, { isLoading }] = useCheckVerificationCodeMutation();

  const disable = isLoading || isAttemptReached;
  const dialogTitle = isAttemptReached ? 'Uh-oh! We were unable to verify your email' : 'We need to verify your email';

  const { errors: validationErrors, handleSubmit, control, setError, setValue } = useForm<
    TOneTimeCodeVerificationFields
  >({
    mode: 'all',
    defaultValues: oneTimeCodeVerificationValidationSchema.cast() as TOneTimeCodeVerificationFields,
    resolver: yupResolver(oneTimeCodeVerificationValidationSchema),
  });

  const handleSubmitCode = ({ code: codeValue }: TOneTimeCodeVerificationFields) => {
    const code = parseInt(codeValue, 10);
    checkVerificationCode({ hashId, code })
      .unwrap()
      .then(() => {
        trackEvent('Email verification code submitted — success');
        dispatch(claimRequest(hashId));
      })
      .catch(({ status, data: { error } }: TResponseQueryError<TErrors>) => {
        if (status === 422 && error?.errors?.code) {
          trackEvent('Email verification code submitted — fail');
          setError(OneTimeCodeFieldNames.Code, {
            type: 'manual',
            message: error?.errors?.code?.join(',') ?? null,
          });
          setHasVerificationError(true);
        }
        if (status === 409) {
          trackEvent('Email verification code submitted — suspicious');
          dispatch(updateStatuses({ maxVerificationAttemptReached: true }));
          setValue(OneTimeCodeFieldNames.Code, null);
        }
      });
  };

  const handleClose = () => {
    dispatch(toggleModal(false));
  };

  const handleSupportLinkClick = () => {
    trackEvent('Email verification — contact support clicked');
  };

  return (
    <Dialog
      fullScreen={isMobile}
      BackdropProps={{ style: !isMobile ? backdropStyles : {} }}
      PaperProps={{
        elevation: 1,
        square: false,
        sx: isAttemptReached ? styles.paperWarning : styles.paperInfo,
      }}
      open={isOpen}
      onClose={handleClose}
      disablePortal
    >
      <DialogTitle sx={styles.title}>
        {dialogTitle}
        <CloseIcon sx={styles.closeIcon} color="action" onClick={handleClose} />
      </DialogTitle>
      <DialogContent>
        <Typography sx={styles.subTitle}>
          {isAttemptReached && (
            <>
              You’ve exceeded the number of verification attempts allowed. If you have questions or think there was a
              mistake, <SupportLink text="contact support" onClick={handleSupportLinkClick} />
            </>
          )}
          {hasVerificationError && !isAttemptReached && (
            <>
              We were unable to verify the email associated with your {customBranding[BrandingFields.companyName]} gift,
              let’s try again. Please check your inbox for verification code sent to <b>[{recipient?.email}]</b>
            </>
          )}
          {!isAttemptReached && !hasVerificationError && (
            <>
              Please check your inbox for verification code sent to <b>[{recipient?.email}]</b>
            </>
          )}
        </Typography>
        <Box mt={4} mb={2}>
          <Typography sx={[styles.subTitle, ...(isAttemptReached ? [styles.disabledText] : [])]}>Enter code</Typography>
        </Box>
        <Box mt={1}>
          <Controller
            control={control}
            name={OneTimeCodeFieldNames.Code}
            render={({ value, onChange }) => (
              <VerificationCodeField
                value={value || ''}
                error={validationErrors.code?.message}
                disabled={disable}
                onChange={onChange}
              />
            )}
          />
        </Box>
        <ResendLink />
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button
          sx={styles.submitButton}
          type="submit"
          variant="contained"
          autoFocus
          disabled={disable}
          onClick={handleSubmit(handleSubmitCode)}
        >
          Verify
        </Button>
      </DialogActions>
      <Box mb={2}>
        <Typography sx={[styles.subTitle, ...(isAttemptReached ? [styles.disabledText] : [])]}>
          Can’t find it? Please check your spam folder.
        </Typography>
      </Box>
    </Dialog>
  );
};

export default OneTimeCodeVerificationModal;
